import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/common/table/table";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RefreshCcwIcon, ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import { Slider } from "@mui/material";

import { Ellipse } from "@/components/common/svgs";
import { clearLoadSearchState, fetchLoads, refreshLoads } from "@/state/loadSearchSlice";
import { Skeleton } from "@/components/common/other/Skeleton";
import { ErrorComponent } from "@/components/common/errors";
import { Pagination } from "@/components/common/pagination/Pagination";
import { LoadboardTableCell, LoadboardTableRow, TableWidths, SortableTableHead } from "@/components/dataPages/loadboard";
import { setSearchType } from "@/state/searchInputSlice";
import { TranslatedText } from "@/components/common/text";
import { useQuery } from "@/utils/query";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { MultiSelectDropdown, SelectDropdown } from "@/components/common/inputs";
import { SelectOnMapModal } from "@/components/common/modal/SelectOnMapModal";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { cn } from "@/utils/css";
import { MarketOverview } from "@/components/home/MarketOverview";

export const Loadboard = () => {
    const dispatch = useDispatch();
    const isDarkTheme = useIsDarkTheme();
    const { loads, loading, error } = useSelector((state) => state.loadSearch);
    const {
        trailerType,
        pickupZip,
        pickupPlaceName,
        pickupStateCode,
        deliveryZip,
        deliveryPlaceName,
        deliveryStateCode,
        pickupDate,
        pickupDateEnd,
        deliveryDate,
        deliveryDateEnd,
        pickupRadius,
        deliveryRadius,
        page,
        sortBy,
        sortDirection,
        pickupMarket,
        perPage,
    } = useQuery();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const getLoadsIntervalRef = useRef(null);

    const LOADS_PER_PAGE_OPTIONS = [
        { value: 48, label: "48" },
        { value: 96, label: "96" },
        { value: 150, label: "150" },
    ];
    const DEFAULT_LOADS_PER_PAGE = 48;
    const itemsPerPage = perPage ? parseInt(perPage) : DEFAULT_LOADS_PER_PAGE;
    const [currentPage, setCurrentPage] = useState(parseInt(page) || 1);

    const [selectedStartMarkets, setSelectedStartMarkets] = useState(
        pickupMarket
            ? {
                  [pickupMarket]: true,
              }
            : {},
    );
    const [selectedEndMarkets, setSelectedEndMarkets] = useState({});
    const [selectedStops, setSelectedStops] = useState("any");
    const [selectedBrokers, setSelectedBrokers] = useState({});
    const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
    const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);

    // Filters visibility state
    const [showFilters, setShowFilters] = useState(false);

    // Range slider states
    const [rpmRange, setRpmRange] = useState(null);
    const [distanceRange, setDistanceRange] = useState(null);
    const [weightRange, setWeightRange] = useState(null);

    // Slider bounds - calculated once from initial data, stored in ref to never change
    const sliderBoundsRef = useRef(null);

    // Calculate bounds only once when data first becomes available
    if (loads.data && loads.data.length > 0 && !sliderBoundsRef.current) {
        let rpmMin = Infinity,
            rpmMax = -Infinity;
        let distMin = Infinity,
            distMax = -Infinity;
        let weightMin = Infinity,
            weightMax = -Infinity;

        loads.data.forEach((load) => {
            if (load.rpm != null) {
                rpmMin = Math.min(rpmMin, load.rpm);
                rpmMax = Math.max(rpmMax, load.rpm);
            }
            if (load.loadDistance != null) {
                distMin = Math.min(distMin, load.loadDistance);
                distMax = Math.max(distMax, load.loadDistance);
            }
            if (load.loadWeight != null) {
                weightMin = Math.min(weightMin, load.loadWeight);
                weightMax = Math.max(weightMax, load.loadWeight);
            }
        });

        sliderBoundsRef.current = {
            rpm: {
                min: rpmMin === Infinity ? 0 : Math.floor(rpmMin),
                max: rpmMax === -Infinity ? 100 : Math.ceil(rpmMax),
            },
            distance: {
                min: distMin === Infinity ? 0 : Math.floor(distMin),
                max: distMax === -Infinity ? 10000 : Math.ceil(distMax),
            },
            weight: {
                min: weightMin === Infinity ? 0 : Math.floor(weightMin),
                max: weightMax === -Infinity ? 100000 : Math.ceil(weightMax),
            },
        };
    }

    const sliderBounds = sliderBoundsRef.current;

    // Initialize slider ranges when bounds are first set
    useEffect(() => {
        if (sliderBounds && rpmRange === null) {
            setRpmRange([sliderBounds.rpm.min, sliderBounds.rpm.max]);
            setDistanceRange([sliderBounds.distance.min, sliderBounds.distance.max]);
            setWeightRange([sliderBounds.weight.min, sliderBounds.weight.max]);
        }
    }, [sliderBounds, rpmRange]);

    const handleRpmSliderChange = (event, newValue) => {
        setRpmRange(newValue);
    };

    const handleDistanceSliderChange = (event, newValue) => {
        setDistanceRange(newValue);
    };

    const handleWeightSliderChange = (event, newValue) => {
        setWeightRange(newValue);
    };

    // Default sort is by age (descending - newest first)
    const currentSort = {
        key: sortBy || "age",
        direction: sortDirection || "desc",
    };

    const handleSort = (key, direction) => {
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set("sortBy", key);
        updatedParams.set("sortDirection", direction);
        updatedParams.set("page", "1"); // Reset to first page when sorting
        navigate(`${location.pathname}?${updatedParams.toString()}`);
    };

    useEffect(() => {
        dispatch(setSearchType("loadboard"));
    }, []);

    const clearLoadsInterval = () => {
        if (getLoadsIntervalRef.current) {
            clearInterval(getLoadsIntervalRef.current);
        }
    };

    const getLoads = () => {
        const pickup = { zip: pickupZip, countryCode: "US", placeName: pickupPlaceName, stateCode: pickupStateCode };
        const delivery = deliveryZip
            ? { zip: deliveryZip, countryCode: "US", placeName: deliveryPlaceName, stateCode: deliveryStateCode }
            : undefined;

        dispatch(
            fetchLoads({
                trailerType,
                pickup,
                delivery,
                pickupDate,
                pickupDateEnd,
                deliveryDate,
                deliveryDateEnd,
                pickupRadius: pickupRadius === "market-area" ? null : pickupRadius,
                deliveryRadius: deliveryRadius === "market-area" ? null : deliveryRadius,
            }),
        );

        clearLoadsInterval();
        getLoadsIntervalRef.current = setInterval(() => {
            dispatch(
                refreshLoads({
                    trailerType,
                    pickup,
                    delivery,
                    pickupDate,
                    pickupDateEnd,
                    deliveryDate,
                    deliveryDateEnd,
                    pickupRadius: pickupRadius === "market-area" ? null : pickupRadius,
                    deliveryRadius: deliveryRadius === "market-area" ? null : deliveryRadius,
                }),
            );
        }, 60000);
    };

    useEffect(() => {
        if (trailerType && pickupZip) {
            getLoads();
        }
    }, [trailerType, pickupZip, deliveryZip, pickupDate, pickupDateEnd, deliveryDate, deliveryDateEnd, pickupRadius, deliveryRadius]);

    useEffect(() => {
        setCurrentPage(parseInt(page) || 1);
    }, [page]);

    // Reset to first page when market filters change
    useEffect(() => {
        if (currentPage !== 1) {
            const updatedParams = new URLSearchParams(searchParams);
            updatedParams.set("page", "1");
            navigate(`${location.pathname}?${updatedParams.toString()}`, { replace: true });
        }
    }, [selectedStartMarkets, selectedEndMarkets, selectedStops, selectedBrokers]);

    const loadMarkets = useMemo(() => {
        if (!loads.data || loads.data.length === 0) {
            return {};
        }

        const startMarkets = {};
        const endMarkets = {};
        const stops = {};
        const brokers = {};

        loads.data.forEach((load) => {
            const firstStop = load.itineraryStops[0];
            const lastStop = load.itineraryStops[load.itineraryStops.length - 1];
            if (firstStop?.marketName) {
                const marketName = firstStop?.marketName;
                startMarkets[marketName] = (startMarkets[marketName] || 0) + 1;
            }
            if (lastStop?.marketName) {
                const marketName = lastStop?.marketName;
                endMarkets[marketName] = (endMarkets[marketName] || 0) + 1;
            }

            // Count stops
            const stopCount = Math.max(0, load.itineraryStops.length - 2);
            const stopKey = stopCount.toString();
            stops[stopKey] = (stops[stopKey] || 0) + 1;

            // Count brokers
            if (load.brokerInfo?.name) {
                const brokerName = load.brokerInfo.name;
                brokers[brokerName] = (brokers[brokerName] || 0) + 1;
            }
        });

        const sortedBrokers = Object.keys(brokers)
            .sort((a, b) => a.localeCompare(b))
            .reduce((acc, key) => {
                acc[key] = brokers[key];
                return acc;
            }, {});

        return {
            startMarkets,
            endMarkets,
            stops,
            brokers: sortedBrokers,
        };
    }, [loads]);

    // All brokers are selected by default; brokers appearing in a refresh start selected too,
    // while explicit user deselections of already-known brokers are preserved
    const knownBrokersRef = useRef(new Set());
    useEffect(() => {
        const newBrokers = Object.keys(loadMarkets.brokers || {}).filter((key) => !knownBrokersRef.current.has(key));
        if (newBrokers.length === 0) {
            return;
        }
        newBrokers.forEach((key) => knownBrokersRef.current.add(key));
        setSelectedBrokers((prev) => {
            const next = { ...prev };
            newBrokers.forEach((key) => {
                next[key] = true;
            });
            return next;
        });
    }, [loadMarkets]);

    // Filter and sort loads based on current sort settings and market filters
    const sortedLoads = useMemo(() => {
        if (!loads.data) return [];

        // Filter loads based on selected markets
        let filteredLoads = [...loads.data];

        // Filter by start markets (pickup)
        const selectedStartMarketKeys = Object.keys(selectedStartMarkets).filter((key) => selectedStartMarkets[key]);
        if (selectedStartMarketKeys.length > 0) {
            filteredLoads = filteredLoads.filter((load) => {
                const firstStop = load.itineraryStops[0];
                return firstStop?.marketName && selectedStartMarketKeys.includes(firstStop.marketName);
            });
        }

        // Filter by end markets (delivery)
        const selectedEndMarketKeys = Object.keys(selectedEndMarkets).filter((key) => selectedEndMarkets[key]);
        if (selectedEndMarketKeys.length > 0) {
            filteredLoads = filteredLoads.filter((load) => {
                const lastStop = load.itineraryStops[load.itineraryStops.length - 1];
                return lastStop?.marketName && selectedEndMarketKeys.includes(lastStop.marketName);
            });
        }

        // Filter by stops
        if (selectedStops && selectedStops !== "any") {
            filteredLoads = filteredLoads.filter((load) => {
                const stopCount = Math.max(0, load.itineraryStops.length - 2);
                if (selectedStops === "no-stops") {
                    return stopCount === 0;
                } else if (selectedStops === "multi-stops") {
                    return stopCount > 0;
                }
                return false;
            });
        }

        // Filter by brokers — all brokers selected is the default state and applies no filtering
        const brokerKeys = Object.keys(loadMarkets.brokers || {});
        const selectedBrokerKeys = brokerKeys.filter((key) => selectedBrokers[key]);
        if (brokerKeys.length > 0 && selectedBrokerKeys.length < brokerKeys.length) {
            filteredLoads = filteredLoads.filter((load) => {
                return load.brokerInfo?.name && selectedBrokerKeys.includes(load.brokerInfo.name);
            });
        }

        // Filter by RPM range
        if (sliderBounds && rpmRange && (rpmRange[0] !== sliderBounds.rpm.min || rpmRange[1] !== sliderBounds.rpm.max)) {
            filteredLoads = filteredLoads.filter((load) => {
                const loadRpm = load.rpm || 0;
                return loadRpm >= rpmRange[0] && loadRpm <= rpmRange[1];
            });
        }

        // Filter by distance range
        if (
            sliderBounds &&
            distanceRange &&
            (distanceRange[0] !== sliderBounds.distance.min || distanceRange[1] !== sliderBounds.distance.max)
        ) {
            filteredLoads = filteredLoads.filter((load) => {
                const loadDistance = load.loadDistance || 0;
                return loadDistance >= distanceRange[0] && loadDistance <= distanceRange[1];
            });
        }

        // Filter by weight range
        if (sliderBounds && weightRange && (weightRange[0] !== sliderBounds.weight.min || weightRange[1] !== sliderBounds.weight.max)) {
            filteredLoads = filteredLoads.filter((load) => {
                const loadWeight = load.loadWeight || 0;
                return loadWeight >= weightRange[0] && loadWeight <= weightRange[1];
            });
        }

        // Sort the filtered loads
        const sorted = filteredLoads.sort((a, b) => {
            let aValue, bValue;

            switch (currentSort.key) {
                case "age":
                    aValue = a.updatedAt || a.createdAt ? dayjs(a.updatedAt || a.createdAt).unix() : 0;
                    bValue = b.updatedAt || b.createdAt ? dayjs(b.updatedAt || b.createdAt).unix() : 0;
                    break;
                case "pickup":
                    aValue = a.itineraryStops[0]?.stopCity || "";
                    bValue = b.itineraryStops[0]?.stopCity || "";
                    break;
                case "delivery":
                    aValue = a.itineraryStops[a.itineraryStops.length - 1]?.stopCity || "";
                    bValue = b.itineraryStops[b.itineraryStops.length - 1]?.stopCity || "";
                    break;
                case "pickupDate":
                    // Combine date with time from itinerary stop
                    if (a.pickupDate) {
                        const pickupStop = a.itineraryStops[0];
                        const pickupTime = pickupStop?.earlyTime || pickupStop?.lateTime || "00:00";
                        aValue = dayjs(`${a.pickupDate} ${pickupTime}`).unix();
                    } else {
                        aValue = 0;
                    }
                    if (b.pickupDate) {
                        const pickupStop = b.itineraryStops[0];
                        const pickupTime = pickupStop?.earlyTime || pickupStop?.lateTime || "00:00";
                        bValue = dayjs(`${b.pickupDate} ${pickupTime}`).unix();
                    } else {
                        bValue = 0;
                    }
                    break;
                case "deliveryDate":
                    // Combine date with time from itinerary stop
                    if (a.deliveryDate) {
                        const deliveryStop = a.itineraryStops[a.itineraryStops.length - 1];
                        const deliveryTime = deliveryStop?.earlyTime || deliveryStop?.lateTime || "00:00";
                        aValue = dayjs(`${a.deliveryDate} ${deliveryTime}`).unix();
                    } else {
                        aValue = 0;
                    }
                    if (b.deliveryDate) {
                        const deliveryStop = b.itineraryStops[b.itineraryStops.length - 1];
                        const deliveryTime = deliveryStop?.earlyTime || deliveryStop?.lateTime || "00:00";
                        bValue = dayjs(`${b.deliveryDate} ${deliveryTime}`).unix();
                    } else {
                        bValue = 0;
                    }
                    break;
                case "distance":
                    aValue = a.loadDistance || 0;
                    bValue = b.loadDistance || 0;
                    break;
                case "broker":
                    aValue = a.brokerInfo?.name || "";
                    bValue = b.brokerInfo?.name || "";
                    break;
                case "offer":
                    aValue = a.offerRate || 0;
                    bValue = b.offerRate || 0;
                    break;
                case "rpm":
                    aValue = a.rpm || 0;
                    bValue = b.rpm || 0;
                    break;
                default:
                    return 0;
            }

            // Handle string comparison
            if (typeof aValue === "string" && typeof bValue === "string") {
                return currentSort.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }

            // Handle numeric comparison
            return currentSort.direction === "asc" ? aValue - bValue : bValue - aValue;
        });

        return sorted;
    }, [
        loads.data,
        loadMarkets,
        currentSort.key,
        currentSort.direction,
        selectedStartMarkets,
        selectedEndMarkets,
        selectedStops,
        selectedBrokers,
        rpmRange,
        distanceRange,
        weightRange,
        sliderBounds,
    ]);

    const totalItems = sortedLoads.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = sortedLoads.slice(startIndex, endIndex);

    useEffect(() => {
        return () => {
            clearLoadsInterval();
            dispatch(clearLoadSearchState());
        };
    }, []);

    useEffect(() => {
        if (error) {
            clearLoadsInterval();
        }
    }, [error]);

    // Count active filter categories
    const activeFilterCount = useMemo(() => {
        let count = 0;

        // pickup markets
        if (Object.keys(selectedStartMarkets).filter((key) => selectedStartMarkets[key]).length > 0) {
            count++;
        }

        // delivery markets
        if (Object.keys(selectedEndMarkets).filter((key) => selectedEndMarkets[key]).length > 0) {
            count++;
        }

        // stops
        if (selectedStops && selectedStops !== "any") {
            count++;
        }

        // brokers — active only when the user deselected some (all selected is the default)
        const brokerKeys = Object.keys(loadMarkets.brokers || {});
        if (brokerKeys.length > 0 && brokerKeys.filter((key) => selectedBrokers[key]).length < brokerKeys.length) {
            count++;
        }

        // RPM slider (if not at default)
        if (sliderBounds && rpmRange && (rpmRange[0] !== sliderBounds.rpm.min || rpmRange[1] !== sliderBounds.rpm.max)) {
            count++;
        }

        // distance slider (if not at default)
        if (
            sliderBounds &&
            distanceRange &&
            (distanceRange[0] !== sliderBounds.distance.min || distanceRange[1] !== sliderBounds.distance.max)
        ) {
            count++;
        }

        // weight slider (if not at default)
        if (sliderBounds && weightRange && (weightRange[0] !== sliderBounds.weight.min || weightRange[1] !== sliderBounds.weight.max)) {
            count++;
        }

        return count;
    }, [loadMarkets, selectedStartMarkets, selectedEndMarkets, selectedStops, selectedBrokers, rpmRange, distanceRange, weightRange, sliderBounds]);

    // Clear all filters
    const handleClearAllFilters = () => {
        setSelectedStartMarkets({});
        setSelectedEndMarkets({});
        setSelectedStops("any");
        const allBrokers = {};
        Object.keys(loadMarkets.brokers || {}).forEach((key) => {
            allBrokers[key] = true;
        });
        setSelectedBrokers(allBrokers);
        if (sliderBounds) {
            setRpmRange([sliderBounds.rpm.min, sliderBounds.rpm.max]);
            setDistanceRange([sliderBounds.distance.min, sliderBounds.distance.max]);
            setWeightRange([sliderBounds.weight.min, sliderBounds.weight.max]);
        }
    };

    // Converts loadboard market data ("Joliet, IL" format) to format ("Joliet")
    const formatMarketCity = (name) => (name.includes(",") ? name.split(",")[0].trim() : name);

    const useMapModalData = (markets, selectedMarkets, setSelectedMarkets) => {
        const { marketNames, outbounds, filterOutbounds, marketLoads } = useMemo(() => {
            const marketNames = [];
            const outbounds = {};
            const filterOutbounds = [];
            const marketLoads = {};
            Object.entries(markets || {}).forEach(([name, count]) => {
                const cityName = formatMarketCity(name);
                marketNames.push(cityName);
                outbounds[cityName] = { filtered: { rpm: 2, rate: 0, confidence: "no_data", distance: 0, sortIndex: 1 } };
                filterOutbounds.push({ marketName: cityName });
                marketLoads[cityName] = count;
            });
            return { marketNames, outbounds, filterOutbounds, marketLoads };
        }, [markets]);

        const excludedMarkets = useMemo(() => {
            const selected = Object.keys(selectedMarkets).filter((k) => selectedMarkets[k]);
            if (selected.length === 0) return [];
            return Object.keys(markets || {})
                .filter((name) => !selected.includes(name))
                .map(formatMarketCity);
        }, [markets, selectedMarkets]);

        const handleToggle = useCallback(
            (cityName) => {
                const dropdownKey = Object.keys(markets || {}).find((k) => formatMarketCity(k) === cityName);
                if (!dropdownKey) return;
                setSelectedMarkets((prev) => {
                    const selected = Object.keys(prev).filter((k) => prev[k]);
                    if (selected.length === 0) {
                        const newValue = {};
                        Object.keys(markets || {}).forEach((key) => {
                            if (key !== dropdownKey) newValue[key] = true;
                        });
                        return newValue;
                    }
                    const newValue = { ...prev };
                    if (newValue[dropdownKey]) delete newValue[dropdownKey];
                    else newValue[dropdownKey] = true;
                    return newValue;
                });
            },
            [markets],
        );

        return { marketNames, excludedMarkets, handleToggle, outbounds, filterOutbounds, marketLoads };
    };

    const pickupMapData = useMapModalData(loadMarkets.startMarkets, selectedStartMarkets, setSelectedStartMarkets);
    const deliveryMapData = useMapModalData(loadMarkets.endMarkets, selectedEndMarkets, setSelectedEndMarkets);

    if (!pickupZip) {
        return <MarketOverview />;
    }

    return (
        <div className="text-cm-text flex flex-col items-center justify-start md:justify-center w-full grow min-h-full max-w-[1440px] pb-[37px]">
            {loading ? (
                <div className="min-h-[354px]">
                    <Skeleton />
                </div>
            ) : error ? (
                <ErrorComponent className="min-h-[354px]" text={error} />
            ) : loads.data ? (
                <div className="w-full">
                    <div className="text-cm-p-text-primary text-lg/[28px] font-medium text-left mb-4 w-full flex flex-row justify-between">
                        <div className="flex flex-row items-center gap-2 pl-4">
                            <Ellipse isAvailable={sortedLoads.filter((l) => !l.isUnavailable).length} />
                            <span>
                                {sortedLoads.length} <TranslatedText>loads available</TranslatedText>
                            </span>
                            <button
                                className="ml-4 text-sm bg-cm-p-surface-container rounded-[38px] py-[6px] px-4 inline-flex items-center justify-center gap-2"
                                onClick={getLoads}
                            >
                                <RefreshCcwIcon className="w-4 text-cm-p-accent" />
                                <TranslatedText>Refresh</TranslatedText>
                            </button>
                            <button
                                className="text-sm font-medium text-cm-p-text-primary bg-cm-p-surface-container rounded-[38px] h-[34px] px-4 inline-flex items-center justify-center gap-2"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <span>
                                    <TranslatedText>Filters</TranslatedText>
                                </span>
                                {activeFilterCount > 0 && (
                                    <span className="bg-cm-p-accent-600 text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                                        {activeFilterCount}
                                    </span>
                                )}
                                <ChevronDown
                                    className={cn("w-4 h-4 text-cm-p-text-primary transition-transform duration-200", {
                                        "rotate-180": showFilters,
                                    })}
                                />
                            </button>
                            {activeFilterCount > 0 && (
                                <button
                                    className="text-sm text-cm-p-text-primary font-medium cursor-pointer"
                                    onClick={handleClearAllFilters}
                                >
                                    <TranslatedText>Clear all</TranslatedText>
                                </button>
                            )}
                        </div>
                    </div>
                    <div
                        className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out", {
                            "grid-rows-[1fr]": showFilters,
                            "grid-rows-[0fr]": !showFilters,
                        })}
                    >
                        <div className="overflow-hidden">
                            <div className="rounded-lg border border-cm-p-outline px-5 py-4 mb-4 flex flex-row flex-wrap xl:flex-nowrap gap-5">
                                {/* RPM Slider */}
                                {sliderBounds && rpmRange && (
                                    <div className="flex flex-col gap-2 w-[235px]">
                                        <div className="text-cm-p-text-primary text-[14px] font-medium text-center">RPM</div>
                                        <div className="w-full bg-cm-p-surface-container rounded-full px-[14px] py-[6px] flex items-center h-[34px] justify-between">
                                            <div className="text-cm-p-text-primary text-sm w-[42px] text-right shrink-0 mr-2">
                                                {new Intl.NumberFormat("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(rpmRange[0])}
                                            </div>
                                            <div className="w-[calc(100%-116px)] range-wrap mr-[6px] flex items-center justify-center">
                                                <Slider
                                                    value={rpmRange}
                                                    onChange={handleRpmSliderChange}
                                                    min={sliderBounds.rpm.min}
                                                    max={sliderBounds.rpm.max}
                                                    step={0.1}
                                                    valueLabelDisplay="off"
                                                />
                                            </div>
                                            <div className="text-cm-p-text-primary text-sm w-[42px] shrink-0 ml-2">
                                                {new Intl.NumberFormat("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }).format(rpmRange[1])}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Distance Slider */}
                                {sliderBounds && distanceRange && (
                                    <div className="flex flex-col gap-2 w-[235px]">
                                        <div className="text-cm-p-text-primary text-[14px] font-medium text-center">Distance (mi)</div>
                                        <div className="w-full bg-cm-p-surface-container rounded-full px-[14px] py-[6px] flex items-center h-[34px] justify-between">
                                            <div className="text-cm-p-text-primary text-sm w-[42px] text-right shrink-0 mr-2">
                                                {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(distanceRange[0])}
                                            </div>
                                            <div className="w-[calc(100%-116px)] range-wrap mr-[6px] flex items-center justify-center">
                                                <Slider
                                                    value={distanceRange}
                                                    onChange={handleDistanceSliderChange}
                                                    min={sliderBounds.distance.min}
                                                    max={sliderBounds.distance.max}
                                                    step={1}
                                                    valueLabelDisplay="off"
                                                />
                                            </div>
                                            <div className="text-cm-p-text-primary text-sm w-[42px] shrink-0 ml-2">
                                                {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(distanceRange[1])}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Weight Slider */}
                                {sliderBounds && weightRange && (
                                    <div className="flex flex-col gap-2 w-[268px]">
                                        <div className="text-cm-p-text-primary text-[14px] font-medium text-center">Weight (lbs)</div>
                                        <div className="w-full bg-cm-p-surface-container rounded-full px-[14px] py-[6px] flex items-center h-[34px] justify-between">
                                            <div className="text-cm-p-text-primary text-sm w-[47px] text-right shrink-0 mr-2">
                                                {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(weightRange[0])}
                                            </div>
                                            <div className="w-[calc(100%-126px)] range-wrap mr-[6px] flex items-center justify-center">
                                                <Slider
                                                    value={weightRange}
                                                    onChange={handleWeightSliderChange}
                                                    min={sliderBounds.weight.min}
                                                    max={sliderBounds.weight.max}
                                                    step={100}
                                                    valueLabelDisplay="off"
                                                />
                                            </div>
                                            <div className="text-cm-p-text-primary text-sm w-[47px] shrink-0 ml-2">
                                                {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(weightRange[1])}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* Pickup Markets */}
                                <div className="flex flex-col gap-2">
                                    <MultiSelectDropdown
                                        label="Pickup markets"
                                        items={loadMarkets.startMarkets}
                                        value={selectedStartMarkets}
                                        setValue={setSelectedStartMarkets}
                                        placeholder="All"
                                        themes={["labelTop", "loadboard"]}
                                        selectAll
                                        minWidth={136}
                                        menuWidth={200}
                                        usePortal
                                        selectOnMap={() => setIsPickupModalOpen(true)}
                                    />
                                </div>
                                {/* Delivery Markets */}
                                <div className="flex flex-col gap-2">
                                    <MultiSelectDropdown
                                        label="Delivery markets"
                                        items={loadMarkets.endMarkets}
                                        value={selectedEndMarkets}
                                        setValue={setSelectedEndMarkets}
                                        placeholder="All"
                                        themes={["labelTop", "loadboard"]}
                                        selectAll
                                        minWidth={136}
                                        menuWidth={196}
                                        usePortal
                                        className="h-[36px] py-2"
                                        selectOnMap={() => setIsDeliveryModalOpen(true)}
                                    />
                                </div>
                                {/* Stops */}
                                <div className="flex flex-col gap-2">
                                    <SelectDropdown
                                        label="Stops"
                                        items={[
                                            { value: "no-stops", label: "No stops" },
                                            { value: "multi-stops", label: "Multi-stops" },
                                            { value: "any", label: "Any" },
                                        ]}
                                        value={selectedStops}
                                        setValue={setSelectedStops}
                                        placeholder="Any"
                                        themes={["labelTop", "loadboard"]}
                                        minWidth={136}
                                        usePortal
                                        className="h-[36px] py-2"
                                    />
                                </div>
                                {/* Brokers */}
                                <div className="flex flex-col gap-2">
                                    <MultiSelectDropdown
                                        label="Brokers"
                                        items={loadMarkets.brokers}
                                        value={selectedBrokers}
                                        setValue={setSelectedBrokers}
                                        placeholder="All"
                                        emptyLabel="None"
                                        themes={["labelTop", "loadboard"]}
                                        selectAll
                                        minWidth={136}
                                        menuWidth={200}
                                        autocomplete
                                        searchPlaceholder="Find broker"
                                        showCount={false}
                                        usePortal
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border border-cm-p-outline rounded-lg overflow-hidden w-full">
                        <Table className="w-full h-12">
                            <TableHeader className="bg-cm-p-surface-container h-12">
                                <TableRow className="h-12 border-0">
                                    <TableHead className={`${TableWidths[0]}`} />
                                    <SortableTableHead
                                        className={`${TableWidths[1]}`}
                                        sortKey="age"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Age
                                    </SortableTableHead>
                                    <TableHead className={`${TableWidths[2]}`}>LoadID</TableHead>

                                    <SortableTableHead
                                        className={`${TableWidths[3]}`}
                                        sortKey="pickup"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Pickup
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[4]}`}
                                        sortKey="delivery"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Delivery
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[5]}`}
                                        sortKey="pickupDate"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Pickup date
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[6]}`}
                                        sortKey="deliveryDate"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Delivery date
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[7]}`}
                                        sortKey="distance"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Distance
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[8]}`}
                                        sortKey="broker"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                    >
                                        Broker
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[9]}`}
                                        sortKey="offer"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                        alignRight
                                    >
                                        Offer
                                    </SortableTableHead>
                                    <SortableTableHead
                                        className={`${TableWidths[10]}`}
                                        sortKey="rpm"
                                        currentSort={currentSort}
                                        onSort={handleSort}
                                        alignRight
                                    >
                                        RPM
                                    </SortableTableHead>
                                    <TableHead className={`${TableWidths[11]}`} />
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {currentItems?.length ? (
                                    currentItems.map((load, index) => {
                                        return <LoadboardTableRow key={load.uniqueId ?? index} load={load} index={index} />;
                                    })
                                ) : (
                                    <TableRow className="h-12 border-0">
                                        <LoadboardTableCell colSpan={11} innerClassName="h-12">
                                            <div className="w-full h-full flex items-center justify-center">
                                                <TranslatedText text="Couldn't find anything for your current search" />
                                            </div>
                                        </LoadboardTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-4 flex items-center relative">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                itemsPerPage={itemsPerPage}
                                totalItems={sortedLoads.length}
                                onPageChange={(newPage) => {
                                    const updatedParams = new URLSearchParams(searchParams);
                                    updatedParams.set("page", newPage);
                                    navigate(`${location.pathname}?${updatedParams.toString()}`);
                                }}
                                itemsLabel="loads"
                            />
                            <div className="absolute right-0">
                                <SelectDropdown
                                    label="Loads per page"
                                    items={LOADS_PER_PAGE_OPTIONS}
                                    value={itemsPerPage}
                                    setValue={(newValue) => {
                                        const updatedParams = new URLSearchParams(searchParams);
                                        updatedParams.set("perPage", newValue);
                                        updatedParams.set("page", "1");
                                        navigate(`${location.pathname}?${updatedParams.toString()}`);
                                    }}
                                    dropUp
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : null}
            <SelectOnMapModal
                isOpen={isPickupModalOpen}
                onClose={() => setIsPickupModalOpen(false)}
                title={`Included pickup markets - ${pickupPlaceName} and ${Object.keys(loadMarkets.startMarkets || {}).length} others`}
                outbounds={pickupMapData.outbounds}
                filterOutbounds={pickupMapData.filterOutbounds}
                pickupMarkets={pickupMapData.marketNames}
                excludedPickupMarkets={pickupMapData.excludedMarkets}
                onTogglePickupMarket={pickupMapData.handleToggle}
                marketLoads={pickupMapData.marketLoads}
            />
            <SelectOnMapModal
                isOpen={isDeliveryModalOpen}
                onClose={() => setIsDeliveryModalOpen(false)}
                title={`Included delivery markets - ${pickupPlaceName} and ${Object.keys(loadMarkets.endMarkets || {}).length} others`}
                outbounds={deliveryMapData.outbounds}
                filterOutbounds={deliveryMapData.filterOutbounds}
                pickupMarkets={deliveryMapData.marketNames}
                excludedPickupMarkets={deliveryMapData.excludedMarkets}
                onTogglePickupMarket={deliveryMapData.handleToggle}
                marketLoads={deliveryMapData.marketLoads}
            />
        </div>
    );
};
